import CloseIcon from '@mui/icons-material/Close'
import EmailIcon from '@mui/icons-material/Email'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import I18n from 'i18n-js'
import MaterialUiPhoneNumber from 'material-ui-phone-number'
import { Loader } from '../../../../components'
import { RECIPIENT_TYPE } from '../../../../utils/enum'
import { isEmail, isPhoneNumber } from '../../../../validators/validator'
import { useStyles } from './styles'

function ReportModal({
  open,
  onClose,
  recipient,
  onSelectRecipient,
  relativeEmail,
  onRelativeEmailChange,
  relativePhone,
  onRelativePhoneChange,
  onSubmit,
  onDownloadClick,
  loading = false,
  title = I18n.t('patient.detail.reportModal.title'),
}) {
  const { styles } = useStyles()

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') onClose?.()
      }}
      aria-labelledby="report-modal-title"
      sx={styles.dialog}
    >
      <DialogTitle id="report-modal-title" sx={styles.titleWrapper}>
        <Typography component="span" variant="h6" sx={styles.title}>
          {title}
        </Typography>
        <IconButton aria-label={I18n.t('patient.detail.reportModal.close')} onClick={onClose} size="small" sx={{ ml: 1 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={styles.dialogContent}>
        <Box sx={styles.twoColumnsLayout}>
          {/* Columna izquierda */}
          <Box sx={styles.leftColumn}>
            {/* Sección destinatario */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <EmailIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                <Typography sx={styles.recipientLabel}>
                  {I18n.t('patient.detail.reportModal.recipientLabel')}
                </Typography>
              </Box>

              <FormControl component="fieldset">
                <RadioGroup
                  value={recipient || ''}
                  onChange={(e) => onSelectRecipient(e.target.value)}
                >
                  <FormControlLabel
                    value={RECIPIENT_TYPE.PATIENT}
                    control={<Radio color="primary" size="small" />}
                    label={I18n.t('patient.detail.reportModal.recipientPatient')}
                  />
                  <FormControlLabel
                    value={RECIPIENT_TYPE.RELATIVE}
                    control={<Radio color="primary" size="small" />}
                    label={I18n.t('patient.detail.reportModal.recipientRelative')}
                  />
                </RadioGroup>
              </FormControl>

              {recipient === RECIPIENT_TYPE.RELATIVE && (
                <Box sx={styles.relativeFields}>
                  <TextField
                    size="small"
                    label={I18n.t('patient.detail.reportModal.relativeEmail')}
                    type="email"
                    value={relativeEmail}
                    onChange={(e) => onRelativeEmailChange(e.target.value)}
                    disabled={loading}
                    fullWidth
                    error={!!relativeEmail && isEmail(relativeEmail)}
                    helperText={
                      !!relativeEmail && isEmail(relativeEmail)
                        ? I18n.t('patient.detail.reportModal.relativeEmailInvalid')
                        : ''
                    }
                  />
                  <MaterialUiPhoneNumber
                    size="small"
                    variant="outlined"
                    label={I18n.t('patient.detail.reportModal.relativePhone')}
                    value={relativePhone}
                    onChange={(value) => onRelativePhoneChange(value)}
                    defaultCountry="fr"
                    autoFormat={false}
                    disabled={loading}
                    fullWidth
                    error={!!relativePhone && !isPhoneNumber(relativePhone)}
                    helperText={
                      !!relativePhone && !isPhoneNumber(relativePhone)
                        ? I18n.t('patient.detail.reportModal.relativePhoneInvalid')
                        : ''
                    }
                  />
                </Box>
              )}
            </Box>

            {/* Botón generar */}
            <Box sx={styles.generateButton}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                disabled={loading}
                onClick={() => typeof onSubmit === 'function' && onSubmit()}
                startIcon={loading ? <Loader removeMH size={16} /> : null}
                sx={{ textTransform: 'none' }}
              >
                {I18n.t('patient.detail.reportModal.generateSecureLink')}
              </Button>
            </Box>
          </Box>

          {/* Divisor vertical */}
          <Box sx={styles.verticalDivider} />

          {/* Columna derecha — descarga */}
          <Box sx={styles.rightColumn}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Tooltip title={I18n.t('patient.detail.reportModal.downloadFile')}>
                <span>
                  <IconButton
                    sx={styles.pdfIconButton}
                    onClick={() => typeof onDownloadClick === 'function' && onDownloadClick()}
                    disabled={loading}
                    aria-label={I18n.t('patient.detail.reportModal.downloadFile')}
                  >
                    <PictureAsPdfIcon sx={{ fontSize: 30 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography sx={styles.pdfIconLabel}>
                {I18n.t('patient.detail.reportModal.downloadFile')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default ReportModal
